import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";

import Reveal from "../Reveal";
import TypeTile from "../TypeTile";
import { fetchMarbleTypes } from "../../lib/api";

// Show up to the first 6 marble types from the live database as the Home preview.
const PREVIEW_LIMIT = 6;

export const CollectionPreview = () => {
  const navigate = useNavigate();
  const [marbleTypes, setMarbleTypes] = useState([]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const data = await fetchMarbleTypes();
        if (mounted) setMarbleTypes(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Failed to load marble types for Home preview:", err);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const preview = marbleTypes.slice(0, PREVIEW_LIMIT);

  if (preview.length === 0) return null;

  return (
    <section
      id="collection-preview"
      data-testid="section-collection-preview"
      className="relative py-16 md:py-24 border-t border-white/5"
    >
      <div className="editorial-container">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-10 md:mb-14">
          <Reveal className="md:col-span-7">
            <div className="flex items-center gap-4 mb-7">
              <span className="h-px w-10 bg-brand-gold/70" />
              <p className="font-sans text-[11px] uppercase tracking-[0.32em] text-brand-gold">
                The Aurevia Collection
              </p>
            </div>
            <h2
              data-testid="collection-heading"
              className="font-serif font-light text-balance text-brand-cream leading-[1.02] tracking-tight"
              style={{ fontSize: "clamp(2rem, 4.4vw, 4rem)" }}
            >
              Curated Marble.{" "}
              <span className="italic text-brand-gold/90">
                From Source to Space.
              </span>
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="md:col-span-5 md:pt-3">
            <p className="font-sans text-base md:text-lg leading-relaxed text-brand-cream/65 max-w-md md:ml-auto">
              A tightly edited selection of the world&apos;s finest marbles -
              every lot photographed, every slab verified.
            </p>
          </Reveal>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6">
          {preview.map((m, i) => (
            <TypeTile key={m.marble_sno} marbleType={m} revealDelay={Math.min(i * 0.05, 0.25)} />
          ))}
        </div>

        <Reveal delay={0.2} className="mt-10 md:mt-14 flex justify-center">
          <button
            type="button"
            onClick={() => navigate("/collection")}
            data-testid="view-full-collection-link"
            className="group inline-flex items-center gap-3 font-sans text-xs uppercase tracking-[0.3em] text-brand-gold transition-colors hover:text-brand-cream"
          >
            <span className="relative pb-1">
              View Full Collection
              <span className="absolute left-0 bottom-0 h-px w-full bg-brand-gold/40 transition-all duration-500 group-hover:bg-brand-cream/70" />
            </span>
            <ArrowRight
              className="h-4 w-4 transition-transform group-hover:translate-x-1"
              strokeWidth={1.5}
            />
          </button>
        </Reveal>
      </div>
    </section>
  );
};

export default CollectionPreview;
